import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/_marketing/privacy")({
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <section className="py-section-sm md:py-section">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="bg-arc-base-white rounded-2xl p-6 sm:p-8 md:p-12 lg:p-16">
          <article className="mx-auto max-w-3xl">
            <header className="mb-8 md:mb-12">
              <h1 className="text-display-sm md:text-display-md text-arc-base-black mb-4 font-semibold md:mb-6">
                Privacy Notice
              </h1>
              <p className="text-arc-gray-600 text-lg">
                ARC Scotland Transitions Services
                <br />
                Transitions Knowledge Hub and E-Learning Platform
              </p>
            </header>

            {/* Table of Contents */}
            <nav aria-label="Table of contents" className="mb-8 md:mb-12">
              <h2 className="text-arc-gray-900 mb-4 text-lg font-semibold">Contents</h2>
              <ol className="text-arc-primary-700 list-decimal space-y-2 pl-5 text-base">
                <li>
                  <a href="#summary" className="hover:underline">
                    Summary
                  </a>
                </li>
                <li>
                  <a href="#contact" className="hover:underline">
                    Our contact details
                  </a>
                </li>
                <li>
                  <a href="#information" className="hover:underline">
                    The type of personal information we collect
                  </a>
                </li>
                <li>
                  <a href="#information-sources" className="hover:underline">
                    How we get the personal information and why we have it
                  </a>
                </li>
                <li>
                  <a href="#lawful-basis" className="hover:underline">
                    Lawful Basis
                  </a>
                </li>
                <li>
                  <a href="#no-data" className="hover:underline">
                    If you do not provide us with personal data
                  </a>
                </li>
                <li>
                  <a href="#storage" className="hover:underline">
                    How we store your personal information
                  </a>
                </li>
                <li>
                  <a href="#retention" className="hover:underline">
                    How long we hold onto your data
                  </a>
                </li>
                <li>
                  <a href="#rights" className="hover:underline">
                    Your data protection rights
                  </a>
                </li>
                <li>
                  <a href="#queries" className="hover:underline">
                    Queries or concerns
                  </a>
                </li>
              </ol>
            </nav>

            <div className="text-arc-gray-700 space-y-8 text-base md:space-y-10 md:text-lg">
              {/* Summary */}
              <section id="summary">
                <h2 className="text-arc-gray-900 mb-4 text-xl font-semibold md:text-2xl">
                  1. Summary
                </h2>
                <p className="mb-4">
                  The{" "}
                  <a
                    href="https://transitions.arcscotland.org.uk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-arc-primary-900 underline"
                  >
                    Transitions Knowledge Hub
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://learn.arcscotland.org.uk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-arc-primary-900 underline"
                  >
                    E-Learning Platform
                  </a>{" "}
                  were created and are operated by ARC Scotland. ARC Scotland set up the Scottish
                  Transition Forum and has been working with the Scottish Government, Local
                  Authorities and other organisations to improve the experience of children and
                  young adults with additional support needs as they make the transition to young
                  adult life.
                </p>
                <p className="mb-4">These services are part of this work:</p>
                <ul className="mb-4 list-disc space-y-2 pl-5">
                  <li>
                    <strong>The Transitions Knowledge Hub</strong> (transitions.arcscotland.org.uk)
                    is an informative website providing resources, guidance and information about
                    transitions. You do not need to sign up to use it.
                  </li>
                  <li>
                    <strong>The E-Learning Platform</strong> provides online training courses for
                    professionals working in this area. You need to create an account to access the
                    courses.
                  </li>
                </ul>
                <p className="mb-4">
                  We may share aggregated and anonymised information with partner organisations –
                  such as local authorities or Scottish Government. Any information shared will be
                  pooled together in a way that means it cannot be linked back to you.
                </p>
                <p>
                  This privacy notice is here to help you understand what we are doing with your
                  personal data; how we are keeping it safe and to tell you about the rights that
                  you have in relation to your personal data. We want you to feel comfortable that
                  the personal information you are giving to us will be looked after.
                </p>
              </section>

              {/* Contact Details */}
              <section id="contact">
                <h2 className="text-arc-gray-900 mb-4 text-xl font-semibold md:text-2xl">
                  2. Our contact details
                </h2>
                <address className="not-italic">
                  <p className="mb-1">
                    <strong>Name:</strong> Association for Real Change
                  </p>
                  <p className="mb-1">
                    <strong>Address:</strong> ARC House, Marden Street, Chesterfield, Derbyshire S40
                    1JY
                  </p>
                  <p>
                    <strong>Email:</strong>{" "}
                    <a
                      href="mailto:contact@arcscotland.org.uk"
                      className="text-arc-primary-900 underline"
                    >
                      contact@arcscotland.org.uk
                    </a>
                  </p>
                </address>
              </section>

              {/* Information We Collect */}
              <section id="information">
                <h2 className="text-arc-gray-900 mb-4 text-xl font-semibold md:text-2xl">
                  3. The type of personal information we collect and what we do with it
                </h2>

                <h3 className="text-arc-gray-900 mt-6 mb-3 text-lg font-semibold">
                  Transitions Knowledge Hub
                </h3>
                <p className="mb-4">
                  You do not need to create an account to use the Knowledge Hub. However, we collect
                  information if you choose to use the following features:
                </p>
                <div className="mb-6 overflow-x-auto">
                  <table className="text-arc-gray-700 w-full border-collapse text-sm md:text-base">
                    <caption className="sr-only">
                      Information collected through the Transitions Knowledge Hub
                    </caption>
                    <thead>
                      <tr className="bg-arc-gray-50 border-arc-gray-200 border-b">
                        <th scope="col" className="px-4 py-3 text-left font-semibold">
                          Type of information
                        </th>
                        <th scope="col" className="px-4 py-3 text-left font-semibold">
                          Purpose
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-arc-gray-200 border-b">
                        <td className="px-4 py-3 font-medium">Mailing list</td>
                        <td className="px-4 py-3">
                          If you sign up to our mailing list, we collect your email address and any
                          other information you provide (such as your name). We use this to send you
                          updates about transitions resources, news and events. You can unsubscribe
                          at any time.
                        </td>
                      </tr>
                      <tr className="border-arc-gray-200 border-b">
                        <td className="px-4 py-3 font-medium">Contact form</td>
                        <td className="px-4 py-3">
                          If you use our contact form, we collect your name, email address and your
                          message. We use this information to respond to your enquiry.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3 className="text-arc-gray-900 mt-6 mb-3 text-lg font-semibold">
                  E-Learning Platform
                </h3>
                <p className="mb-4">
                  We collect and use the following information through the E-Learning Platform:
                </p>
                <div className="overflow-x-auto">
                  <table className="text-arc-gray-700 w-full border-collapse text-sm md:text-base">
                    <caption className="sr-only">
                      Information collected through the E-Learning Platform
                    </caption>
                    <thead>
                      <tr className="bg-arc-gray-50 border-arc-gray-200 border-b">
                        <th scope="col" className="px-4 py-3 text-left font-semibold">
                          Type of information
                        </th>
                        <th scope="col" className="px-4 py-3 text-left font-semibold">
                          Purpose
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-arc-gray-200 border-b">
                        <td className="px-4 py-3 font-medium">Account information</td>
                        <td className="px-4 py-3">
                          When you use the platform, you create a profile which includes your name
                          and email address. This allows you to access your account and use the
                          platform. We will send you password reminders or other essential account
                          information by email.
                        </td>
                      </tr>
                      <tr className="border-arc-gray-200 border-b">
                        <td className="px-4 py-3 font-medium">Profile information</td>
                        <td className="px-4 py-3">
                          We may ask you to provide additional information such as your job title,
                          the sector you work in and your local authority area. This helps us
                          understand who is using the platform.
                        </td>
                      </tr>
                      <tr className="border-arc-gray-200 border-b">
                        <td className="px-4 py-3 font-medium">Learning progress</td>
                        <td className="px-4 py-3">
                          As you work through courses, we save your progress so you can pick up
                          where you left off. We record which modules you have completed, your
                          assessment scores and any bookmarks.
                        </td>
                      </tr>
                      <tr className="border-arc-gray-200 border-b">
                        <td className="px-4 py-3 font-medium">Certificates</td>
                        <td className="px-4 py-3">
                          When you complete a course, we generate a certificate for you. We keep a
                          record of the certificates you have earned so you can download them again
                          if needed.
                        </td>
                      </tr>
                      <tr className="border-arc-gray-200 border-b">
                        <td className="px-4 py-3 font-medium">Course evaluations</td>
                        <td className="px-4 py-3">
                          After completing a course, we ask you to provide feedback about your
                          learning experience. This helps us improve our courses.
                        </td>
                      </tr>
                      <tr className="border-arc-gray-200 border-b">
                        <td className="px-4 py-3 font-medium">Mailing list</td>
                        <td className="px-4 py-3">
                          If you opt in to receive updates, we will add you to our mailing list. We
                          use this to send you information about new courses and resources. You can
                          unsubscribe at any time.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Cookies */}
              <section id="information-sources">
                <h2 className="text-arc-gray-900 mb-4 text-xl font-semibold md:text-2xl">
                  4. How we get the personal information and why we have it
                </h2>
                <p className="mb-4">
                  The only source of information about you is from you. We do not gather any more
                  information about you from other places. It is only the information that you give
                  to us that we use.
                </p>
                <p className="mb-4">
                  You may have heard about something called Cookies. These allow websites and Apps
                  to function better but can also be used to gather a lot of information about how
                  you behave online. We use the following cookies:
                </p>

                <h3 className="text-arc-gray-900 mt-6 mb-3 text-lg font-semibold">
                  Essential Cookies
                </h3>
                <p className="mb-2">
                  These cookies are necessary for our services to work properly:
                </p>
                <ul className="mb-4 list-disc space-y-2 pl-5">
                  <li>
                    Session cookies that keep you signed in while you use the E-Learning Platform.
                    Your login session is stored in a cookie so you do not have to sign in again
                    each time you visit a new page;
                  </li>
                  <li>
                    Temporary security cookies used during sign-in to keep your account safe (these
                    are deleted immediately after you sign in); and
                  </li>
                  <li>
                    SCORM data that saves your progress within course modules so you can pick up
                    where you left off.
                  </li>
                </ul>

                <h3 className="text-arc-gray-900 mt-6 mb-3 text-lg font-semibold">
                  Analytics Cookies
                </h3>
                <p className="mb-4">
                  We use a service called PostHog on both the Knowledge Hub and the E-Learning
                  Platform to help us understand how people use our services. This helps us to
                  improve our websites and make them work better for everyone. PostHog collects
                  information such as:
                </p>
                <ul className="mb-4 list-disc space-y-2 pl-5">
                  <li>Which pages you visit and how long you spend on them;</li>
                  <li>How you navigate through our websites;</li>
                  <li>Any errors you encounter;</li>
                  <li>Short survey questionnaire responses; and</li>
                  <li>General information about your device and browser.</li>
                </ul>
                <p className="mb-4">
                  This information is used to create reports and help us understand patterns in how
                  our services are used. PostHog is a processor and is bound by a legal contract
                  which means they cannot do anything with your information unless we have
                  instructed them to. They store data securely in the European Union.
                </p>
                <p>
                  You can manage your cookie preferences at any time from your{" "}
                  <Link to="/profile" className="text-arc-primary-900 underline">
                    profile settings
                  </Link>
                  .
                </p>
              </section>

              {/* Lawful Basis */}
              <section id="lawful-basis">
                <h2 className="text-arc-gray-900 mb-4 text-xl font-semibold md:text-2xl">
                  5. Lawful Basis
                </h2>
                <p className="mb-4">
                  Under the UK General Data Protection Regulation (UK GDPR), we need to have a
                  lawful basis to use your information. We have set these out below.
                </p>
                <div className="overflow-x-auto">
                  <table className="text-arc-gray-700 w-full border-collapse text-sm md:text-base">
                    <caption className="sr-only">
                      Lawful basis for processing your data under UK GDPR
                    </caption>
                    <thead>
                      <tr className="bg-arc-gray-50 border-arc-gray-200 border-b">
                        <th scope="col" className="px-4 py-3 text-left font-semibold">
                          Lawful Basis for collecting the data
                        </th>
                        <th scope="col" className="px-4 py-3 text-left font-semibold">
                          Purpose
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-arc-gray-200 border-b">
                        <td className="px-4 py-3 font-medium">Contractual Obligation</td>
                        <td className="px-4 py-3">
                          We process Account Information to provide you with a user account on the
                          E-Learning Platform. We save your learning progress and issue certificates
                          as part of providing the e-learning service to you.
                        </td>
                      </tr>
                      <tr className="border-arc-gray-200 border-b">
                        <td className="px-4 py-3 font-medium">Legitimate Interest</td>
                        <td className="px-4 py-3">
                          We use analytics data and anonymised feedback to improve our services and
                          understand how professionals engage with our resources. We believe this is
                          a good reason for us to process this data but we have balanced this
                          against your privacy interests. Therefore, we only share aggregated and
                          anonymised information. We also process contact form enquiries to respond
                          to your questions.
                        </td>
                      </tr>
                      <tr className="border-arc-gray-200 border-b">
                        <td className="px-4 py-3 font-medium">Consent</td>
                        <td className="px-4 py-3">
                          Where you sign up to our mailing list or choose to receive newsletters, we
                          will only send these because you have given us permission. You can
                          withdraw this permission at any time by clicking the unsubscribe link in
                          any email we send you.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* No Personal Data */}
              <section id="no-data">
                <h2 className="text-arc-gray-900 mb-4 text-xl font-semibold md:text-2xl">
                  6. If you do not provide us with personal data
                </h2>
                <p className="mb-4">
                  You can browse the Knowledge Hub without providing any personal data. If you wish
                  to sign up to our mailing list or contact us, you will need to provide the
                  information requested.
                </p>
                <p>
                  For the E-Learning Platform, if you do not wish to provide the information we ask
                  for, the platform will not work properly and we may be unable to provide you with
                  access to our courses.
                </p>
              </section>

              {/* Storage */}
              <section id="storage">
                <h2 className="text-arc-gray-900 mb-4 text-xl font-semibold md:text-2xl">
                  7. How we store your personal information
                </h2>
                <p className="mb-4">
                  We work with the following technology providers who help us to operate our
                  services. These organisations are legally called processors which means that they
                  provide us with the services we asked them to, under a legal contract. The terms
                  of these contracts mean that they cannot do anything with your information unless
                  we have instructed them to do that. They will not share your personal information
                  with any organisation apart from us or as instructed by us. They will hold it
                  securely and only for as long as we instruct them to.
                </p>
                <ul className="mb-4 list-disc space-y-2 pl-5">
                  <li>
                    <strong>Microsoft Azure:</strong> hosts the E-Learning Platform and stores the
                    information gathered securely.
                  </li>
                  <li>
                    <strong>WorkOS:</strong> handles signing in to the E-Learning Platform securely.
                  </li>
                  <li>
                    <strong>Mailchimp:</strong> manages our mailing lists for both the Knowledge Hub
                    and E-Learning Platform. Mailchimp stores your email address and any other
                    information you provide when signing up. Mailchimp is based in the United States
                    and complies with data protection requirements through Standard Contractual
                    Clauses.
                  </li>
                  <li>
                    <strong>PostHog:</strong> provides analytics for both services to help us
                    understand how they are used. They store data securely in the European Union.
                  </li>
                </ul>
                <p>
                  The information gathered by our services is encrypted when it is transferred
                  between your device and our systems. It is also encrypted as it is stored.
                </p>
              </section>

              {/* Retention */}
              <section id="retention">
                <h2 className="text-arc-gray-900 mb-4 text-xl font-semibold md:text-2xl">
                  8. How long we hold onto your data
                </h2>

                <h3 className="text-arc-gray-900 mt-6 mb-3 text-lg font-semibold">
                  E-Learning Platform
                </h3>
                <p className="mb-4">
                  We keep your account open until you tell us to close it. Or if it is five years
                  since you have used your account, we will contact you and unless you tell us
                  otherwise, we will close your account. We will contact you using the email address
                  you provided to us. Once your account is closed, your personal information will be
                  securely deleted from our database.
                </p>
                <p className="mb-4">
                  We will retain your course evaluation responses, but these will be anonymised.
                  This will continue to assist us to improve our training courses.
                </p>

                <h3 className="text-arc-gray-900 mt-6 mb-3 text-lg font-semibold">Mailing list</h3>
                <p className="mb-4">
                  We keep your information on our mailing list until you unsubscribe. You can
                  unsubscribe at any time by clicking the unsubscribe link in any email we send you,
                  or by contacting us.
                </p>

                <h3 className="text-arc-gray-900 mt-6 mb-3 text-lg font-semibold">
                  Contact form enquiries
                </h3>
                <p>
                  We keep contact form enquiries for as long as necessary to deal with your enquiry
                  and for a reasonable period afterwards in case of follow-up questions.
                </p>
              </section>

              {/* Rights */}
              <section id="rights">
                <h2 className="text-arc-gray-900 mb-4 text-xl font-semibold md:text-2xl">
                  9. Your data protection rights
                </h2>
                <p className="mb-4">Under data protection law, you have rights including:</p>
                <div className="overflow-x-auto">
                  <table className="text-arc-gray-700 w-full border-collapse text-sm md:text-base">
                    <caption className="sr-only">Your data protection rights under UK law</caption>
                    <thead>
                      <tr className="bg-arc-gray-50 border-arc-gray-200 border-b">
                        <th scope="col" className="px-4 py-3 text-left font-semibold">
                          Your Rights
                        </th>
                        <th scope="col" className="px-4 py-3 text-left font-semibold">
                          What they mean in practice
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-arc-gray-200 border-b">
                        <td className="px-4 py-3 font-medium">
                          Request access to your personal information
                        </td>
                        <td className="px-4 py-3">
                          Commonly known as a "data subject access request". This enables you to
                          receive a copy of the personal information we hold about you and to check
                          that we are lawfully processing it.
                        </td>
                      </tr>
                      <tr className="border-arc-gray-200 border-b">
                        <td className="px-4 py-3 font-medium">
                          Request correction of the personal information that we hold about you
                        </td>
                        <td className="px-4 py-3">
                          This enables you to have any incomplete or inaccurate information we hold
                          about you corrected.
                        </td>
                      </tr>
                      <tr className="border-arc-gray-200 border-b">
                        <td className="px-4 py-3 font-medium">
                          Request erasure of your personal information
                        </td>
                        <td className="px-4 py-3">
                          This enables you to ask us to delete or remove personal information where
                          there is no good reason for us continuing to process it. You also have the
                          right to ask us to delete or remove your personal information where you
                          have exercised your right to object to processing.
                        </td>
                      </tr>
                      <tr className="border-arc-gray-200 border-b">
                        <td className="px-4 py-3 font-medium">
                          Request the restriction of processing of your personal information
                        </td>
                        <td className="px-4 py-3">
                          You would make this request if, for example, you want to establish its
                          accuracy or the reason for processing it. You would generally ask for this
                          restriction when you are asking for other information.
                        </td>
                      </tr>
                      <tr className="border-arc-gray-200 border-b">
                        <td className="px-4 py-3 font-medium">Your right to data portability</td>
                        <td className="px-4 py-3">
                          You have the right to ask that we transfer the personal information you
                          gave us to another organisation, or to you, in certain circumstances.
                        </td>
                      </tr>
                      <tr className="border-arc-gray-200 border-b">
                        <td className="px-4 py-3 font-medium">
                          Object to the processing of your personal information
                        </td>
                        <td className="px-4 py-3">
                          If we are relying on your consent, or a legitimate interest and there is
                          something about your particular situation which makes you want to object
                          to processing on this basis.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="mt-4">
                  You are not required to pay anything for exercising your rights. If you make a
                  request, we have one month to respond to you.
                </p>
              </section>

              {/* Queries */}
              <section id="queries">
                <h2 className="text-arc-gray-900 mb-4 text-xl font-semibold md:text-2xl">
                  10. Queries or concerns
                </h2>
                <p className="mb-4">
                  Please contact us using the contact details above to make a request or to raise
                  any concerns you have about how we use your personal data.
                </p>
                <p>
                  You can also contact the Information Commissioner's Office if you have concerns
                  and their contact details can be found here:{" "}
                  <a
                    href="https://ico.org.uk/global/contact-us/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-arc-primary-900 underline"
                  >
                    ico.org.uk/global/contact-us/
                  </a>
                  .
                </p>
              </section>

              {/* Last Updated */}
              <footer className="border-arc-gray-200 mt-8 border-t pt-6 md:mt-12">
                <p className="text-arc-gray-500 text-sm">
                  <strong>Last Updated:</strong> January 2026
                </p>
              </footer>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
